import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { noteActions } from '_actions';
import { CheckBox, StatusWarning } from '_components';
import { codeValidForAdmit } from '_helpers';
import styles from './WpAlTemplate.module.css';

// Constants for entity types and column names
const ENTITIES = {
    WPALTEMPLATE: 'WPALTEMPLATE',
    WPALTEMPLATEHEADERS: 'WPALTEMPLATEHEADERS',
    WPALTEMPLATEITEMS: 'WPALTEMPLATEITEMS'
};

const COLUMNS = {
    WPCNO: 'WPCNO',
    WPSNO: 'WPSNO',
    XLTMPWPNO: 'XLTMPWPNO'
};


class AlWpTemplateRow extends React.Component {

    // Helper function to toggle an item if its state differs from target
    toggleItemIfNeeded = (entity, column, value, targetState, currentState) => {
        if (!!currentState !== targetState) {
            this.props.toggleItem({
                entity,
                entityKeyColumn: column,
                entityKeyValue: value,
                selected: targetState
            });
        }
    };

    // Helper to calculate expected parent state based on children
    calculateParentState = (children, toggledChild, newToggledState, childKey) => {
        return children.every(child => {
            if (child[childKey] === toggledChild[childKey]) {
                return newToggledState;
            }
            return !!child.selected;
        });
    };

    handleWpcToggle = () => {
        const { wpc } = this.props;
        const newWpcState = !wpc.selected;

        // Toggle main header
        this.props.toggleItem({
            entity: ENTITIES.WPALTEMPLATE,
            entityKeyColumn: COLUMNS.WPCNO,
            entityKeyValue: wpc.WPCNO,
            selected: newWpcState
        });

        // Set all child headers and items to match the parent state
        wpc.children.forEach(wps => {
            this.toggleItemIfNeeded(
                ENTITIES.WPALTEMPLATEHEADERS,
                COLUMNS.WPSNO,
                wps.WPSNO,
                newWpcState,
                wps.selected
            );

            wps.children.forEach(item => {
                this.toggleItemIfNeeded(
                    ENTITIES.WPALTEMPLATEITEMS,
                    COLUMNS.XLTMPWPNO,
                    item.XLTMPWPNO,
                    newWpcState,
                    item.selected
                );
            });
        });
        // Set all orphan items to match the parent state
        if (wpc.orphanItems) {
            wpc.orphanItems.forEach(item => {
                this.toggleItemIfNeeded(
                    ENTITIES.WPALTEMPLATEITEMS,
                    COLUMNS.XLTMPWPNO,
                    item.XLTMPWPNO,
                    newWpcState,
                    item.selected
                );
            });
        }
    };

    handleWpsToggle = (wps) => {
        const { wpc } = this.props;
        const newWpsState = !wps.selected;

        // Toggle WPS header
        this.props.toggleItem({
            entity: ENTITIES.WPALTEMPLATEHEADERS,
            entityKeyColumn: COLUMNS.WPSNO,
            entityKeyValue: wps.WPSNO,
            selected: newWpsState
        });

        // Set all items to match the WPS state
        wps.children.forEach(item => {
            this.toggleItemIfNeeded(
                ENTITIES.WPALTEMPLATEITEMS,
                COLUMNS.XLTMPWPNO,
                item.XLTMPWPNO,
                newWpsState,
                item.selected
            );
        });

        // Update WPC parent state
        const expectedWpcState = this.calculateParentState(wpc.children, wps, newWpsState, 'WPSNO');
        this.toggleItemIfNeeded(
            ENTITIES.WPALTEMPLATE,
            COLUMNS.WPCNO,
            wpc.WPCNO,
            expectedWpcState,
            wpc.selected
        );
    };

    handleItemToggle = (item, wps) => {
        const { wpc } = this.props;
        const newItemState = !item.selected;

        // Toggle the item
        this.props.toggleItem({
            entity: ENTITIES.WPALTEMPLATEITEMS,
            entityKeyColumn: COLUMNS.XLTMPWPNO,
            entityKeyValue: item.XLTMPWPNO,
            selected: newItemState
        });

        // Update WPS parent state
        const expectedWpsState = this.calculateParentState(wps.children, item, newItemState, 'XLTMPWPNO');
        this.toggleItemIfNeeded(
            ENTITIES.WPALTEMPLATEHEADERS,
            COLUMNS.WPSNO,
            wps.WPSNO,
            expectedWpsState,
            wps.selected
        );

        // Update WPC parent state
        const expectedWpcState = this.calculateParentState(wpc.children, wps, expectedWpsState, 'WPSNO');
        this.toggleItemIfNeeded(
            ENTITIES.WPALTEMPLATE,
            COLUMNS.WPCNO,
            wpc.WPCNO,
            expectedWpcState,
            wpc.selected
        );
    };

    handleOrphanItemToggle = (item) => {
        const { wpc } = this.props;
        const newItemState = !item.selected;

        // Toggle the orphan item
        this.props.toggleItem({
            entity: ENTITIES.WPALTEMPLATEITEMS,
            entityKeyColumn: COLUMNS.XLTMPWPNO,
            entityKeyValue: item.XLTMPWPNO,
            selected: newItemState
        });

        // Only update WPC parent state if this is not an orphan section
        if (!wpc.isOrphanSection) {
            // Update WPC parent state based on all children (including orphan items)
            const allChildren = [...wpc.children, ...(wpc.orphanItems || [])];
            const expectedWpcState = this.calculateParentState(allChildren, item, newItemState, 'XLTMPWPNO');
            this.toggleItemIfNeeded(
                ENTITIES.WPALTEMPLATE,
                COLUMNS.WPCNO,
                wpc.WPCNO,
                expectedWpcState,
                wpc.selected
            );
        }
    };

    handleOrphanSelectAll = () => {
        const { wpc } = this.props;
        const orphanItems = wpc.orphanItems || [];

        if (orphanItems.length === 0) return;

        // Calculate current state - if all are selected, deselect all; otherwise select all
        const allSelected = orphanItems.every(item => !!item.selected);
        const newState = !allSelected;

        // Toggle all orphan items
        orphanItems.forEach(item => {
            this.toggleItemIfNeeded(
                ENTITIES.WPALTEMPLATEITEMS,
                COLUMNS.XLTMPWPNO,
                item.XLTMPWPNO,
                newState,
                item.selected
            );
        });
    };

    render() {
        const { wpc, codeValid } = this.props;


        return (
            <div className={styles.section}>
                {/* Main WPC Header - only show if not an orphan section */}
                {!wpc.isOrphanSection && (
                    <div className={styles.row}>
                        <CheckBox
                            size='extra-small'
                            className={styles.checkbox}
                            checked={!!wpc.selected}
                            title={wpc.WPCNAME}
                            label={<span className={styles.headerText}>{wpc.WPCNAME}</span>}
                            onClick={this.handleWpcToggle}
                        />
                    </div>
                )}

                {/* Orphan section header */}
                {wpc.isOrphanSection && (
                    <div className={styles.row}>
                        <CheckBox
                            size='extra-small'
                            className={styles.checkbox}
                            checked={wpc.orphanItems && wpc.orphanItems.length > 0 && wpc.orphanItems.every(item => !!item.selected)}
                            title={wpc.WPCNAME}
                            label={<span className={styles.headerText}>{wpc.WPCNAME}</span>}
                            onClick={this.handleOrphanSelectAll}
                        />
                    </div>
                )}

                {/* Sub headers (WPS) and Items */}
                {wpc.children.map(wps => (
                    <div key={wps.WPSNO}>
                        <div className={styles.row + ' ' + styles.subSection}>
                            <CheckBox
                                size='extra-small'
                                className={styles.checkboxWps}
                                checked={!!wps.selected}
                                title={wps.WPSNAME}
                                label={wps.WPSNAME}
                                onClick={() => this.handleWpsToggle(wps)}
                            />
                        </div>

                        {wps.children.map(item => {
                            // Build description from activity and sets/reps
                            const parts = [item.XLTMPWPACTIVITY, item.XLTMPWPSETSREPS].filter(Boolean);
                            const desc = parts.join(' - ');

                            return (
                                <div key={item.XLTMPWPNO} className={styles.row + ' ' + styles.itemRow}>
                                    <CheckBox
                                        size='extra-small'
                                        className={styles.checkbox}
                                        checked={!!item.selected}
                                        title={desc}
                                        label={desc}
                                        onClick={() => this.handleItemToggle(item, wps)}
                                    />
                                    {!codeValid && (
                                        <span className={styles.warning}>
                                            <StatusWarning size='extra-small' title='Not Reimbursable' />
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}

                {/* Items with no parent header */}
                {wpc.orphanItems && wpc.orphanItems.length > 0 && (
                    <div>
                        {wpc.orphanItems.map(item => {
                            // Build description from activity and sets/reps
                            const parts = [item.XLTMPWPACTIVITY, item.XLTMPWPSETSREPS].filter(Boolean);
                            const desc = parts.join(' - ');

                            return (
                                <div key={item.XLTMPWPNO} className={styles.row + ' ' + styles.itemRow}>
                                    <CheckBox
                                        size='extra-small'
                                        className={styles.checkbox}
                                        checked={!!item.selected}
                                        title={desc}
                                        label={desc}
                                        onClick={() => this.handleOrphanItemToggle(item)}
                                    />
                                    {!codeValid && (
                                        <span className={styles.warning}>
                                            <StatusWarning size='extra-small' title='Not Reimbursable' />
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        toggleItem: bindActionCreators(noteActions.alTemplateToggleItem, dispatch)
    };
}

function mapStateToProps(state) {
    const selectedPtno = state.ui.patients.selectedPtno;
    const selectedTab = state.ui.patientCenter[selectedPtno].selectedTab;
    const noteId = state.ui.patientTabs[selectedTab].noteId;

    const [noteTypeStr, pkStr] = noteId.split("_");
    const noteType = parseInt(noteTypeStr);
    const pk = parseInt(pkStr);

    const epno = noteType === 1
        ? state.entities.notes.PTEVAL[pk].EVEPNO
        : state.entities.notes.PTNOTE[pk].NTEPNO;

    // Check if any of the valid codes are applicable
    const validCodes = ['97545', '97545E', '97546', '97546E'];
    const codeValid = validCodes.some(code => codeValidForAdmit(epno, code));

    return {
        noteId,
        codeValid
    };
}

const connectedAlWpTemplateRow = connect(mapStateToProps, mapDispatchToProps)(AlWpTemplateRow);
export { connectedAlWpTemplateRow as AlWpTemplateRow };

